import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "./colors";
import { PageContainer } from "./PageContainer";

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const HERO_BG = "#080808";

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const SECTION_SX = {
  textAlign: "center",
  paddingTop: "8rem",
  paddingBottom: "8rem",
  px: { xs: 2, md: 4 },
  width: "100%",
  backgroundColor: HERO_BG,
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background: "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": { opacity: 1 },
  "&:active": { transform: "translateY(0px)" },
};

const TITLE_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.25rem", sm: "2.75rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  "& span": {
    background: `
      linear-gradient(
        180deg,
        #f7cb79 0%,
        #f0b458 40%,
        #de8f2c 100%
      )
    `,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
};

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(3, minmax(0, 1fr))",
  },
  gap: "1.5rem",
  mt: { xs: "2.5rem", md: "3rem" },
  maxWidth: "68rem",
  mx: "auto",
};

const BILLING_SELECTOR_SX = {
  mt: { xs: "2.5rem", md: "3rem" },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const BILLING_SELECTOR_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: { xs: 1, sm: 1.25, md: 1.5 },
};

const BILLING_CONTROL_SX = {
  position: "relative",
  display: "flex",
  padding: "4px",
  border: "1px solid rgba(255,255,255,0.08)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  minWidth: 260,
  height: 48,
  boxSizing: "border-box",
};

const BILLING_LABEL_SX = {
  position: "relative",
  zIndex: 1,
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.74rem",
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  border: 0,
  background: "transparent",
  cursor: "pointer",
  userSelect: "none",
  px: 1.5,
  py: 0,
};

const SAVE_PROMO_BADGE_SX = {
  height: 48,
  px: 1.4,
  display: "flex",
  alignItems: "center",
  border: "1px solid rgba(232,163,61,0.18)",
  background: "rgba(232,163,61,0.04)",
  color: colors.primary.main,
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  boxSizing: "border-box",
};

const PLAN_BADGE_SX = {
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "flex-start",
  px: 1,
  py: 0.35,
  border: `0.5px solid ${colors.primary.main}`,
  fontSize: "0.62rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: colors.primary.main,
};

const CARD_SX = {
  position: "relative",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1.5,
  width: "100%",
  minWidth: 0,
  height: { md: "100%" },
  p: { xs: "2rem 1.5rem", md: "2.5rem 2rem" },
  border: `0.5px solid ${colors.border.default}`,
  backgroundColor: "transparent",
  transition: "border-color 0.25s, transform 0.25s",
  "@media (hover: hover)": {
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
};

const CARD_FEATURED_SX = {
  borderColor: "rgba(200, 135, 42, 0.3)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(to right, transparent, #C8872A, transparent)",
    pointerEvents: "none",
  },
};

const FEATURE_LIST_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1.55,
  width: "100%",
};

const FEATURE_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.6,
  width: "100%",
  fontSize: "0.8rem",
  lineHeight: 1.45,
  color: colors.text.primary,
  "&::before": {
    content: '""',
    display: "block",
    width: 4,
    height: 4,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: colors.primary.main,
  },
};

const DIVIDER_SX = {
  width: "100%",
  height: "0.5px",
  backgroundColor: colors.border.default,
  flexShrink: 0,
};

const PLANS = [
  {
    key: "free",
    tier: "Tier 01",
    name: "Free",
    monthlyPrice: "€0",
    annualMonthlyPrice: "€0",
    annualCompareAt: null,
    annualTotal: null,
    annualBadge: null,
    cta: "Get Started",
    featured: false,
    features: [
      { text: "Unlimited matchmaking access", active: true },
      { text: "Default matchmaking settings", active: true },
      { text: "All maps and locations active", active: true },
      { text: "Standard queue placement", active: true },
    ],
  },
  {
    key: "premium",
    tier: "Tier 02",
    name: "Premium",
    monthlyPrice: "€9.15",
    annualMonthlyPrice: "€6.99",
    annualCompareAt: "€9.15",
    annualTotal: "€83.88",
    annualBadge: "Best value",
    cta: "Upgrade",
    featured: true,
    features: [
      { text: "Full matchmaking settings access", active: true },
      { text: "Premium tier servers", active: true },
      { text: "Refine down to 3 active maps", active: true },
      { text: "Refine down to 3 active server locations", active: true },
      { text: "Expanded queue control", active: true },
      { text: "Premium profile badge", active: true },
    ],
  },
  {
    key: "pro",
    tier: "Tier 03",
    name: "Pro",
    monthlyPrice: "€16.99",
    annualMonthlyPrice: "€12.99",
    annualCompareAt: "€16.99",
    annualTotal: "€155.88",
    annualBadge: "Save 24%",
    cta: "Go Pro",
    featured: false,
    features: [
      { text: "Everything in Premium", active: true },
      { text: "Premium tier servers", active: true },
      { text: "Captain priority in team", active: true },
      { text: "ELO recalibration requests", active: true },
      { text: "Can refine down to 1 active map", active: true },
      { text: "Can refine down to 1 active server location", active: true },
      { text: "Priority queue placement", active: true },
      { text: "Pro profile badge", active: true },
    ],
  },
];

function FeatureList({ features }) {
  const included = features.filter((f) => f.active);

  return (
    <Box sx={FEATURE_LIST_SX}>
      {included.map((feature) => (
        <Typography key={feature.text} component="div" sx={FEATURE_SX}>
          {feature.text}
        </Typography>
      ))}
    </Box>
  );
}

function BillingCycleToggle({ isAnnual, onChange, disabled = false }) {
  const setMonthly = () => {
    if (!disabled) {
      onChange(false);
    }
  };

  const setAnnual = () => {
    if (!disabled) {
      onChange(true);
    }
  };

  return (
    <Box sx={BILLING_SELECTOR_SX}>
      <Box
        sx={{
          width: 48,
          height: "1px",
          background: colors.primary.main,
          mx: "auto",
          mb: 1.5,
          opacity: 0.6,
        }}
      />

      <Box sx={BILLING_SELECTOR_ROW_SX}>
        <Box sx={BILLING_CONTROL_SX}>
          <MotionBox
            aria-hidden
            animate={{
              x: isAnnual ? "100%" : "0%",
            }}
            transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
              position: "absolute",
              top: 4,
              left: 4,
              width: "calc(50% - 4px)",
              height: "calc(100% - 8px)",
              background: `
                linear-gradient(
                  180deg,
                  rgba(232,163,61,0.16),
                  rgba(232,163,61,0.08)
                )
              `,
              border: "1px solid rgba(232,163,61,0.2)",
              pointerEvents: "none",
            }}
          />

          <MotionTypography
            component="button"
            type="button"
            onClick={setMonthly}
            disabled={disabled}
            animate={{
              color: !isAnnual ? colors.text.primary : colors.text.muted,
            }}
            transition={{ duration: 0.25 }}
            sx={{
              ...BILLING_LABEL_SX,
              cursor: disabled ? "default" : "pointer",
            }}
          >
            Monthly
          </MotionTypography>

          <MotionTypography
            component="button"
            type="button"
            onClick={setAnnual}
            disabled={disabled}
            animate={{
              color: isAnnual ? colors.text.primary : colors.text.muted,
            }}
            transition={{ duration: 0.25 }}
            sx={{
              ...BILLING_LABEL_SX,
              cursor: disabled ? "default" : "pointer",
            }}
          >
            Annual
          </MotionTypography>
        </Box>

        <Box sx={SAVE_PROMO_BADGE_SX}>Save up to 24%</Box>
      </Box>

      <Typography
        sx={{
          mt: 1.5,
          fontSize: "0.8rem",
          color: colors.text.muted,
          textAlign: "center",
        }}
      >
        Annual billing saves up to 24% compared to monthly.
      </Typography>
    </Box>
  );
}

BillingCycleToggle.propTypes = {
  isAnnual: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function PlanPricing({ plan, isAnnual }) {
  const displayPrice = isAnnual ? plan.annualMonthlyPrice : plan.monthlyPrice;
  const showAnnualDetails =
    isAnnual && plan.annualCompareAt && plan.annualTotal;

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      {isAnnual && plan.annualBadge ? (
        <Box sx={{ ...PLAN_BADGE_SX, mb: 1.25 }}>{plan.annualBadge}</Box>
      ) : null}

      <Typography
        component="div"
        sx={{
          fontFamily: "ui-monospace, monospace",
          fontSize: "0.9rem",
          color: colors.text.secondary,
        }}
      >
        <Box
          component="strong"
          sx={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: "2rem",
            fontWeight: 700,
            letterSpacing: "-0.04em",
            lineHeight: 1,
            color: colors.text.primary,
            mr: 0.5,
          }}
        >
          {displayPrice}
        </Box>
        / month
      </Typography>

      {showAnnualDetails ? (
        <>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: "0.85rem",
              color: colors.text.muted,
              textDecoration: "line-through",
            }}
          >
            {plan.annualCompareAt} / month
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: "0.85rem",
              color: colors.text.muted,
            }}
          >
            Billed{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                color: colors.primary.main,
              }}
            >
              {plan.annualTotal}
            </Box>{" "}
            / year
          </Typography>
        </>
      ) : null}
    </Box>
  );
}

PlanPricing.propTypes = {
  plan: PropTypes.shape({
    annualMonthlyPrice: PropTypes.string,
    monthlyPrice: PropTypes.string,
    annualCompareAt: PropTypes.string,
    annualTotal: PropTypes.string,
    annualBadge: PropTypes.string,
  }).isRequired,
  isAnnual: PropTypes.bool.isRequired,
};

function PlanCard({ plan, index, isAnnual }) {
  const featured = plan.featured;

  return (
    <MotionBox
      initial={REVEAL.initial}
      whileInView={REVEAL.whileInView}
      viewport={REVEAL.viewport}
      transition={{
        duration: 0.9,
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1],
      }}
      sx={{ width: "100%", minWidth: 0 }}
    >
      <Box sx={[CARD_SX, featured && CARD_FEATURED_SX]}>
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 600,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: colors.text.muted,
        }}
      >
        {plan.tier}
      </Typography>

      <Typography
        sx={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: "2rem",
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: featured ? colors.primary.main : colors.text.primary,
          ...(featured && {
            background: `
              linear-gradient(
                180deg,
                #f7cb79 0%,
                #f0b458 40%,
                #de8f2c 100%
              )
            `,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }),
        }}
      >
        {plan.name}
      </Typography>

      <PlanPricing plan={plan} isAnnual={isAnnual} />

      <Box sx={DIVIDER_SX} />

      <Box sx={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <FeatureList features={plan.features} />
      </Box>

      {/* <Button
        fullWidth
        sx={{
          mt: 3,
          py: 1.1,
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          borderRadius: 0,
          border: `0.5px solid ${colors.border.default}`,
          color: featured ? "#0a0600" : colors.text.secondary,
          backgroundColor: featured ? colors.primary.main : "transparent",
          "&:hover": {
            borderColor: colors.primary.main,
            color: featured ? "#0a0600" : colors.primary.main,
            backgroundColor: featured ? colors.primary.hover : "transparent",
          },
        }}
      >
        {plan.cta}
      </Button> */}
      </Box>
    </MotionBox>
  );
}

PlanCard.propTypes = {
  plan: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isAnnual: PropTypes.bool.isRequired,
};

function PricingPlanGrid({ defaultAnnual = false }) {
  const [isAnnual, setIsAnnual] = React.useState(defaultAnnual);

  return (
    <>
      <BillingCycleToggle isAnnual={isAnnual} onChange={setIsAnnual} />
      <Box sx={GRID_SX}>
        {PLANS.map((plan, index) => (
          <PlanCard key={plan.key} plan={plan} index={index} isAnnual={isAnnual} />
        ))}
      </Box>
    </>
  );
}

PricingPlanGrid.propTypes = {
  defaultAnnual: PropTypes.bool,
};

export default function OuroTiers() {
  return (
    <Box component="section" id="membership" sx={SECTION_SX}>
      <PageContainer>
        <MotionBox
          {...REVEAL}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.8rem",
              mb: 1.5,
            }}
          >
            <Box sx={{ width: 24, height: "1px", background: colors.primary.main }} />
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 600,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: colors.primary.main,
              }}
            >
              Access Tiers
            </Typography>
          </Box>
          <Typography component="h2" sx={TITLE_SX}>
            Competitive Integrity
            <br />
            Is <span>Free.</span>
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              mx: "auto",
              maxWidth: "36rem",
              fontSize: "1rem",
              lineHeight: 1.85,
              color: colors.text.secondary,
            }}
          >
            Control is premium.
          </Typography>
        </MotionBox>

        <PricingPlanGrid defaultAnnual />

        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 5, md: 6 } }}>
          <Box component={Link} to="/pricing" sx={btnGhostSx}>
            Learn more →
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
